import styles from './NewAgentTaskForm.module.css'
import {clsx} from 'clsx'
import {TaskComposer} from '@github-ui/agent-sessions/components/new-task-chat-input/TaskComposer'
import type {
  CreateTaskReference,
  TaskComposerProps,
} from '@github-ui/agent-sessions/components/new-task-chat-input/shared'
import type {DropdownRepository} from '@github-ui/agent-sessions/hooks/useCopilotRepoFetching'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {SkeletonBox} from '@primer/react'
import {useCurrentRepository, type Repository} from '@github-ui/current-repository'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useCopilotChatPayloadQuery} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayloadQuery'
import type {CreateAPITaskParams} from '@github-ui/agent-sessions/types/api-task'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import type {HandleUserSubmitOptions} from '@github-ui/agent-sessions/types/user-submit'

export type NewAgentTaskFormProps = {
  createTask: (task: CreateAPITaskParams) => void
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  onCreate?: () => void
  showHeader?: boolean
  hideRepoSelector?: boolean
  eventType: string
  textAreaRef?: TaskComposerProps['textAreaRef']
  className?: string
  initialText?: string
  initialBranch?: string
  /** Maximum height for the textarea input in pixels */
  maxTextareaHeight?: number
  /** Whether or not to use task mode - when false this should default to using the 'fix' agent and creating a pull request */
  taskModeEnabled?: boolean
  onEnvironmentSubmit?: TaskComposerProps['onEnvironmentSubmit']
  supportsManagedSandbox?: TaskComposerProps['supportsManagedSandbox']
}

export function NewAgentTaskForm({
  createTask,
  currentRepo,
  currentReference,
  onCreate,
  showHeader = true,
  hideRepoSelector,
  eventType,
  textAreaRef,
  className,
  initialText,
  initialBranch,
  maxTextareaHeight,
  taskModeEnabled,
  onEnvironmentSubmit,
  supportsManagedSandbox,
}: NewAgentTaskFormProps) {
  const copilotChatPayload = useCopilotChatPayloadQuery()
  const agentQuotaSnapshot = copilotChatPayload?.agentQuotaSnapshot
  const currentRepoFromContext = useCurrentRepository()
  const initialRepo = currentRepo || currentRepoFromContext

  const handleUserSubmit = async (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    modelId?: string,
    options?: HandleUserSubmitOptions,
  ) => {
    const trimmedContent = content.trim()
    if (trimmedContent === '') return
    if (!selectedRepo) return
    const newTask: CreateAPITaskParams = {
      nwo: selectedRepo.nameWithOwner,
      problemStatement: trimmedContent,
      baseRef: selectedBranch || selectedRepo.defaultBranch,
      eventType,
      eventContent: trimmedContent,
      createPullRequest: options?.createPullRequest ?? (taskModeEnabled === false ? true : undefined),
      mcpPermissionMode: options?.mcpPermissionMode,
      model: modelId,
      reasoningEffort: options?.reasoningEffort,
      customAgent: selectedAgent?.subAgentSlug,
      agentId: selectedAgent?.topLevelAgentId,
    }
    createTask(newTask)

    onCreate?.()
  }

  return (
    <>
      {showHeader && (
        <div className={styles.header}>
          <h2 className="f5">New agent session</h2>
        </div>
      )}
      <div className={clsx(styles.content, !showHeader && 'tmp-pt-3', className)}>
        {copilotChatPayload ? (
          <CopilotChatProvider
            threadId={null}
            refs={copilotChatPayload.refs || []}
            mode="immersive"
            ssoOrganizations={copilotChatPayload.ssoOrganizations || []}
            copilotChatPayload={copilotChatPayload}
          >
            <TaskComposer
              handleUserSubmit={handleUserSubmit}
              initialRepo={initialRepo}
              initialReference={currentReference}
              hideRepoSelector={hideRepoSelector}
              textAreaRef={textAreaRef}
              eventType={eventType}
              initialText={initialText}
              initialBranch={initialBranch}
              maxTextareaHeight={maxTextareaHeight}
              agentQuotaSnapshot={agentQuotaSnapshot}
              onEnvironmentSubmit={onEnvironmentSubmit}
              supportsManagedSandbox={supportsManagedSandbox}
              mcpPermissionGateEnabled={copilotChatPayload.mcpPermissionGateEnabled}
              currentUserLogin={copilotChatPayload.currentUserLogin}
            />
          </CopilotChatProvider>
        ) : (
          <SkeletonBox className={styles.skeleton} />
        )}
      </div>
    </>
  )
}
